import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  useToast,
  useBreakpointValue,
  IconButton,
  Select,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import {
  MagnifyingGlass,
  Ticket,
  Plus,
  List,
} from '@phosphor-icons/react'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import socketService, { SOCKET_EVENTS } from '../../services/socket'
import { Sidebar } from '../../layouts/DefaultLayout'
import CreateTicketModal from './CreateTicketModal'
import { TicketDetailView, TicketItem } from './index'
import type { InternalTicket, TicketCategory } from './index'

export default function MyInternalTickets() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(id ? Number(id) : null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()

  const currentUserId = user?.id || 0

  // Tickets list state
  const [tickets, setTickets] = useState<InternalTicket[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [categories, setCategories] = useState<TicketCategory[]>([])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/internal-ticket-categories')
        setCategories(response.data || [])
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }
    fetchCategories()
  }, [])

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true)
    try {
      const params: Record<string, string | number | undefined> = {
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        search: debouncedSearch || undefined,
        limit: 50,
      }
      const response = await api.get('/internal-tickets/my', { params })
      setTickets(response.data.data)
    } catch (error) {
      if (!silent) console.error('Error fetching my internal tickets:', error)
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [statusFilter, categoryFilter, debouncedSearch, refreshTrigger])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  useEffect(() => {
    const pollInterval = setInterval(() => fetchTickets(true), 90000)
    return () => clearInterval(pollInterval)
  }, [fetchTickets])

  // Badge
  const fetchInternalTicketsCount = useCallback(async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (id) {
      setSelectedTicketId(Number(id))
    }
  }, [id])

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()
    const handleTransferred = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)
    }
  }, [fetchInternalTicketsCount])

  // Socket: toasts + refresh list
  useEffect(() => {
    const handleNewTicket = (data: any) => {
      toast({
        title: 'Novo ticket interno',
        description: `${data.creatorName}: ${data.subject}`,
        status: 'info',
        duration: 5000,
        position: 'top-right',
      })
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleReply = () => {
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleResolved = () => {
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleTransferred = (data: any) => {
      toast({
        title: 'Ticket transferido',
        description: data.subject ? `Ticket "${data.subject}" foi transferido para você` : 'Um ticket foi transferido para você',
        status: 'info',
        duration: 5000,
        position: 'top-right',
      })
      setRefreshTrigger((prev) => prev + 1)
    }

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNewTicket)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)

    return () => {
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNewTicket)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)
    }
  }, [toast])

  const handleSelectTicket = (ticket: InternalTicket) => {
    setSelectedTicketId(ticket.id)
    navigate(`/my-internal-tickets/${ticket.id}`)
  }

  const handleBack = () => {
    setSelectedTicketId(null)
    navigate('/my-internal-tickets')
  }

  const handleTicketCreated = () => {
    setIsCreateOpen(false)
    setRefreshTrigger((prev) => prev + 1)
  }

  const showSidebar = !isMobile || !selectedTicketId

  return (
    <>
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Flex h="100%" overflow="hidden" direction={{ base: 'column', lg: 'row' }}>
        {showSidebar && (
          <Box
            w={{ base: '100%', lg: '350px' }}
            minW={{ base: '100%', lg: '300px', xl: '350px' }}
            maxW={{ base: '100%', lg: '400px' }}
            bg={surfaceBg}
            borderRight={{ base: 'none', lg: '1px' }}
            borderBottom={{ base: '1px', lg: 'none' }}
            borderColor={borderColor}
            overflow="hidden"
            h="100%"
            minH={0}
            flexShrink={0}
          >
            <VStack h="100%" spacing={0}>
              <HStack w="100%" p={{ base: 3, md: 4 }} borderBottom="1px" borderColor={borderColor} justify="space-between" spacing={2}>
                <HStack spacing={3} align="center" flex={1} minW={0}>
                  {isMobile && (
                    <IconButton
                      aria-label="Menu"
                      icon={<List size={20} />}
                      variant="ghost"
                      onClick={onMenuOpen}
                      size="sm"
                      color={menuIconColor}
                      _hover={{ bg: borderColor }}
                      flexShrink={0}
                    />
                  )}
                  <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textPrimary}>
                    Meus Tickets
                  </Text>
                </HStack>
                <Button
                  size={{ base: 'xs', md: 'sm' }}
                  leftIcon={<Plus size={14} weight="bold" />}
                  bg="yellow.500"
                  color="gray.900"
                  _hover={{ bg: 'yellow.400' }}
                  onClick={() => setIsCreateOpen(true)}
                  flexShrink={0}
                >
                  <Text display={{ base: 'none', sm: 'block' }}>Novo</Text>
                </Button>
              </HStack>

              {/* Filters */}
              <Box px={{ base: 1, md: 2 }} pt={2} flexShrink={0} w="100%">
                <VStack spacing={2}>
                  <InputGroup size={{ base: 'xs', md: 'sm' }}>
                    <InputLeftElement>
                      <MagnifyingGlass size={16} color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Buscar..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      variant="outline"
                      fontSize={{ base: 'xs', md: 'sm' }}
                    />
                  </InputGroup>
                  <Select
                    size={{ base: 'xs', md: 'sm' }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    bg={subtleBg}
                    borderColor={borderSubtle}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    <option value="">Todos</option>
                    <option value="not_responded">Não Respondidos</option>
                    <option value="responded">Respondidos</option>
                    <option value="open">Aberto</option>
                    <option value="in_progress">Em Andamento</option>
                    <option value="waiting_response">Aguardando Resposta</option>
                    <option value="resolved">Concluído</option>
                  </Select>
                  <Select
                    size={{ base: 'xs', md: 'sm' }}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    bg={subtleBg}
                    borderColor={borderSubtle}
                    fontSize={{ base: 'xs', md: 'sm' }}
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </VStack>
              </Box>

              {/* Ticket list */}
              <VStack spacing={1} align="stretch" overflowY="auto" flex={1} minH={0} px={2} pb={2} pt={2}>
                {isLoading ? (
                  <Flex justify="center" p={8}>
                    <Spinner size="lg" color="yellow.500" />
                  </Flex>
                ) : tickets.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py={8}>
                    <Ticket size={40} color="#4A5568" weight="duotone" />
                    <Text color={textMuted} textAlign="center" mt={3} fontSize="sm">
                      Nenhum ticket encontrado
                    </Text>
                  </Flex>
                ) : (
                  tickets.map((ticket) => (
                    <TicketItem
                      key={ticket.id}
                      ticket={ticket}
                      isSelected={ticket.id === selectedTicketId}
                      onClick={() => handleSelectTicket(ticket)}
                      currentUserId={currentUserId}
                    />
                  ))
                )}
              </VStack>
            </VStack>
          </Box>
        )}

        {selectedTicketId ? (
          <Box flex={1} overflow="hidden" bg={pageBg} minW={0}>
            <TicketDetailView
              ticketId={selectedTicketId}
              currentUserId={currentUserId}
              onBack={handleBack}
              onTicketUpdated={() => setRefreshTrigger((prev) => prev + 1)}
            />
          </Box>
        ) : (
          !isMobile && (
            <Flex flex={1} align="center" justify="center" bg={pageBg} minW={0}>
              <VStack spacing={4} px={4}>
                <Ticket size={64} color="#4A5568" weight="duotone" />
                <Text color={textMuted} fontSize="lg" textAlign="center">
                  Selecione um ticket para visualizar
                </Text>
              </VStack>
            </Flex>
          )
        )}

        <CreateTicketModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={handleTicketCreated}
        />
      </Flex>
    </>
  )
}
